import { effect, Injectable, Signal, signal } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { map } from 'rxjs/operators';

import { GrowthBook } from '@growthbook/growthbook';

import { OrderformFascade } from '../fascades/orderform.fascade';
import { MonitoringService } from './monitoring.service';

export enum GrowthBookFeature {
  ACCESSIBILITY_TOGGLE = 'accessibility-toggle',
}

interface GrowthBookFeatures {
  'accessibility-toggle': boolean;
}

@Injectable({
  providedIn: 'root',
})
export class GrowthBookService {
  private readonly overrideParam = 'feature_override';
  private readonly apiHost = 'https://cdn.growthbook.io';
  private readonly features = signal<GrowthBookFeatures>({
    'accessibility-toggle': false,
  });
  private DS_GLOBAL_GROWTHBOOK_TOKEN: Signal<string>;
  private growthbook: GrowthBook | null = null;
  siteowner_id = toSignal(this.orderformFacade.config$.pipe(map((config) => config.siteowner_id)));

  constructor(
    private readonly orderformFacade: OrderformFascade,
    private readonly monitoringService: MonitoringService
  ) {
    effect(
      () => {
        this.setAttributes({ siteowner_id: this.siteowner_id() });
        this.updateFeatures();
      },
      { allowSignalWrites: true }
    );
  }

  async initialize(): Promise<void> {
    try {
      this.DS_GLOBAL_GROWTHBOOK_TOKEN = toSignal(
        this.orderformFacade.config$.pipe(map((config) => config.ds_global_growthbook_token))
      );
      const featureOverrideCookie = this.getCookie(this.overrideParam);

      this.growthbook = new GrowthBook({
        apiHost: this.apiHost,
        clientKey: this.DS_GLOBAL_GROWTHBOOK_TOKEN(),
        attributes: {
          feature_override: featureOverrideCookie,
          siteowner_id: this.siteowner_id(),
        },
      });

      await this.growthbook.init();

      this.updateFeatures();
    } catch (error) {
      this.monitoringService.trackValidationError(error, 'frontend', 'GrowthBookService');
    }
  }

  private getCookie(name: string): string | null {
    const value = `; ${document.cookie}`;
    const parts = value.split(`; ${name}=`);

    if (parts.length === 2) return parts.pop().split(';').shift();

    return null;
  }

  isFeatureEnabled(featureName: keyof GrowthBookFeatures): boolean {
    return this.features()[featureName];
  }

  private setAttributes(attributes: Record<string, any>): void {
    if (!this.growthbook) return;

    this.growthbook.setAttributes({
      ...this.growthbook.getAttributes(),
      ...attributes,
    });
  }

  private updateFeatures(): void {
    if (!this.growthbook) return;

    const updatedFeatures: GrowthBookFeatures = {
      'accessibility-toggle': this.growthbook.isOn(GrowthBookFeature.ACCESSIBILITY_TOGGLE),
    };

    this.features.set(updatedFeatures);
  }
}
